(() => {
  if (window.BasedSingleAccordion) {
    window.BasedSingleAccordion.init();
    return;
  }

  const containerSelector = '[data-based-single-accordion]';
  const animationDuration = 180;
  const animationEasing = 'cubic-bezier(0.2, 0, 0.2, 1)';
  const animationTokens = new WeakMap();

  const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const getSummary = (item) =>
    Array.from(item.children).find((child) => child instanceof HTMLElement && child.tagName === 'SUMMARY');

  const getPanel = (item) =>
    Array.from(item.children).find((child) => child instanceof HTMLElement && child.tagName !== 'SUMMARY');

  const setItemState = (item, isOpen) => {
    const summary = getSummary(item);

    item.dataset.basedAccordionState = isOpen ? 'open' : 'closed';
    summary?.setAttribute('aria-expanded', String(isOpen));
  };

  const getNextAnimationToken = (item) => {
    const token = (animationTokens.get(item) || 0) + 1;
    animationTokens.set(item, token);
    return token;
  };

  const isCurrentAnimation = (item, token) => animationTokens.get(item) === token;

  const getPanelMetrics = (panel) => {
    const styles = getComputedStyle(panel);

    return {
      height: panel.getBoundingClientRect().height,
      paddingTop: styles.paddingTop,
      paddingBottom: styles.paddingBottom,
    };
  };

  const resetPanelStyles = (panel) => {
    panel.style.boxSizing = '';
    panel.style.height = '';
    panel.style.opacity = '';
    panel.style.overflow = '';
    panel.style.paddingBottom = '';
    panel.style.paddingTop = '';
  };

  const cancelPanelAnimations = (panel) => {
    panel.getAnimations?.().forEach((animation) => animation.cancel());
  };

  const animateItem = (item, shouldOpen) => {
    const panel = getPanel(item);

    if (!shouldOpen && !item.open) {
      setItemState(item, false);
      return;
    }

    if (!panel || prefersReducedMotion() || typeof panel.animate !== 'function') {
      setItemState(item, shouldOpen);
      item.open = shouldOpen;
      return;
    }

    const token = getNextAnimationToken(item);
    const wasOpen = item.open;
    const currentMetrics = getPanelMetrics(panel);
    cancelPanelAnimations(panel);
    item.dataset.basedAccordionAnimating = 'true';
    setItemState(item, shouldOpen);

    if (shouldOpen) {
      const startHeight = wasOpen ? currentMetrics.height : 0;
      const startPaddingTop = wasOpen ? currentMetrics.paddingTop : '0px';
      const startPaddingBottom = wasOpen ? currentMetrics.paddingBottom : '0px';

      item.open = true;
      resetPanelStyles(panel);
      const targetHeight = panel.scrollHeight;
      const targetMetrics = getPanelMetrics(panel);

      panel.style.boxSizing = 'border-box';
      panel.style.overflow = 'hidden';
      panel.style.height = `${startHeight}px`;
      panel.style.opacity = startHeight > 0 ? '1' : '0';
      panel.style.paddingBottom = startPaddingBottom;
      panel.style.paddingTop = startPaddingTop;

      const animation = panel.animate(
        [
          {
            height: `${startHeight}px`,
            opacity: startHeight > 0 ? 1 : 0,
            paddingBottom: startPaddingBottom,
            paddingTop: startPaddingTop,
          },
          {
            height: `${targetHeight}px`,
            opacity: 1,
            paddingBottom: targetMetrics.paddingBottom,
            paddingTop: targetMetrics.paddingTop,
          },
        ],
        { duration: animationDuration, easing: animationEasing }
      );

      panel.style.height = `${targetHeight}px`;
      panel.style.opacity = '1';
      panel.style.paddingBottom = targetMetrics.paddingBottom;
      panel.style.paddingTop = targetMetrics.paddingTop;

      animation.finished
        .catch(() => {})
        .then(() => {
          if (!isCurrentAnimation(item, token)) return;

          item.open = true;
          delete item.dataset.basedAccordionAnimating;
          resetPanelStyles(panel);
        });

      return;
    }

    if (!item.open) return;

    const targetHeight = currentMetrics.height || panel.scrollHeight;
    panel.style.boxSizing = 'border-box';
    panel.style.overflow = 'hidden';
    panel.style.height = `${targetHeight}px`;
    panel.style.opacity = '1';
    panel.style.paddingBottom = currentMetrics.paddingBottom;
    panel.style.paddingTop = currentMetrics.paddingTop;

    const animation = panel.animate(
      [
        {
          height: `${targetHeight}px`,
          opacity: 1,
          paddingBottom: currentMetrics.paddingBottom,
          paddingTop: currentMetrics.paddingTop,
        },
        { height: '0px', opacity: 0, paddingBottom: '0px', paddingTop: '0px' },
      ],
      { duration: animationDuration, easing: animationEasing }
    );

    panel.style.height = '0px';
    panel.style.opacity = '0';
    panel.style.paddingBottom = '0px';
    panel.style.paddingTop = '0px';

    animation.finished
      .catch(() => {})
      .then(() => {
        if (!isCurrentAnimation(item, token)) return;

        item.open = false;
        delete item.dataset.basedAccordionAnimating;
        resetPanelStyles(panel);
      });
  };

  const closeSiblings = (container, openedItem) => {
    Array.from(container.children).forEach((item) => {
      if (item instanceof HTMLDetailsElement && item !== openedItem && item.open) {
        animateItem(item, false);
      }
    });
  };

  const initContainer = (container) => {
    if (!(container instanceof HTMLElement) || container.dataset.basedSingleAccordionReady === 'true') return;

    container.dataset.basedSingleAccordionReady = 'true';
    Array.from(container.children).forEach((item) => {
      if (item instanceof HTMLDetailsElement) {
        setItemState(item, item.open);
      }
    });

    container.addEventListener('click', (event) => {
      if (prefersReducedMotion() || !(event.target instanceof Element)) return;

      const summary = event.target.closest('summary');
      const item = summary?.parentElement;

      if (!(item instanceof HTMLDetailsElement) || item.closest(containerSelector) !== container) return;

      event.preventDefault();

      if (item.open) {
        animateItem(item, false);
        return;
      }

      closeSiblings(container, item);
      animateItem(item, true);
    });

    container.addEventListener(
      'toggle',
      (event) => {
        const openedItem = event.target;

        if (!(openedItem instanceof HTMLDetailsElement)) return;
        if (openedItem.closest(containerSelector) !== container) return;
        if (openedItem.dataset.basedAccordionAnimating === 'true') return;

        setItemState(openedItem, openedItem.open);
        if (!openedItem.open) return;

        Array.from(container.children).forEach((item) => {
          if (item instanceof HTMLDetailsElement && item !== openedItem && item.open) {
            animateItem(item, false);
          }
        });
      },
      true
    );
  };

  const init = (root = document) => {
    if (root instanceof HTMLElement && root.matches(containerSelector)) {
      initContainer(root);
    }

    root.querySelectorAll?.(containerSelector).forEach(initContainer);
  };

  window.BasedSingleAccordion = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
